import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';

interface FilterOption {
  value: string;
  label: string;
}

interface SetupFilterBarProps {
  brand: BrandConfig;
  Icons: IconsType;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  statusFilter?: string;
  statusOptions?: FilterOption[];
  onStatusChange?: (value: string) => void;
  categoryFilter?: string;
  categoryOptions?: FilterOption[];
  onCategoryChange?: (value: string) => void;
  onAdd?: () => void;
  addLabel?: string;
}

const SetupFilterBar: React.FC<SetupFilterBarProps> = ({
  brand,
  Icons,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  statusFilter,
  statusOptions,
  onStatusChange,
  categoryFilter,
  categoryOptions,
  onCategoryChange,
  onAdd,
  addLabel = 'Add New',
}) => {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      {/* Search */}
      <div className="relative flex-1">
        <Icons.Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
          style={{ color: brand.colors.textLight }}
        />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
          style={{
            borderColor: brand.colors.border,
            // @ts-expect-error CSS custom property
            '--tw-ring-color': brand.colors.primary,
          }}
        />
      </div>

      {/* Status Filter */}
      {statusOptions && onStatusChange && (
        <select
          value={statusFilter || 'all'}
          aria-label="Status filter"
          onChange={(e) => onStatusChange(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
          style={{
            borderColor: brand.colors.border,
            color: brand.colors.text,
            // @ts-expect-error CSS custom property
            '--tw-ring-color': brand.colors.primary,
          }}
        >
          <option value="all">All Status</option>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {/* Category Filter */}
      {categoryOptions && onCategoryChange && (
        <select
          value={categoryFilter || 'all'}
          aria-label="Category filter"
          onChange={(e) => onCategoryChange(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
          style={{
            borderColor: brand.colors.border,
            color: brand.colors.text,
            // @ts-expect-error CSS custom property
            '--tw-ring-color': brand.colors.primary,
          }}
        >
          <option value="all">All Categories</option>
          {categoryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {/* Add Button */}
      {onAdd && (
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
          style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
        >
          <Icons.Plus className="w-4 h-4" />
          {addLabel}
        </button>
      )}
    </div>
  );
};

export default SetupFilterBar;
